import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { cx } from '@orn/shared-web/utils/cx';

import { channelHealthBadge, channelHealthTooltip } from './channelHealthView';

import type { ChannelHealthFieldsFragment } from './__generated__/channelHealthFields.gql';
import type { ChannelHealthTone } from './channelHealthView';

const TONE_CLASS: Record<ChannelHealthTone, string> = {
  success: 'bg-success-primary text-fg-success-primary',
  warning: 'bg-warning-primary text-fg-warning-primary',
  error: 'bg-error-primary text-fg-error-primary',
  neutral: 'border border-border-primary text-fg-tertiary',
};

interface IChannelHealthBadgeProps {
  health: ChannelHealthFieldsFragment;
}

/**
 * A channel's health state as a tinted word. Hovering it says what the state
 * means for the channel and what lifts it, then the stamps behind it.
 */
const ChannelHealthBadge = ({ health }: IChannelHealthBadgeProps) => {
  const { label, tone } = channelHealthBadge(health.state);
  const tooltip = channelHealthTooltip(health);

  return (
    <Tooltip
      title={tooltip.title}
      description={
        <span className="flex flex-col gap-0.5">
          {tooltip.lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </span>
      }
      placement="top"
      wide
    >
      {/* The trigger hides its outline, so the ring is restored for keyboard focus. */}
      <TooltipTrigger
        aria-label={tooltip.title}
        className="focus-visible:outline-border-brand focus-visible:outline-solid focus-visible:outline-2"
      >
        <span
          className={cx(
            'whitespace-nowrap px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
            TONE_CLASS[tone]
          )}
        >
          {label}
        </span>
      </TooltipTrigger>
    </Tooltip>
  );
};

export default ChannelHealthBadge;
